// StackedBarChart.js
import React, { useRef, useEffect, useMemo } from "react";
import styled from "styled-components";
import Chart from "chart.js/auto";
import { setProperty } from "../util/util";
import ChartDataLabels from "chartjs-plugin-datalabels";

const ChartContainer = styled.div`
	position: relative;
	width: 100%;
	margin: 0 auto;
	padding: 20px;
	background: white;
	border-radius: 12px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const InfoMessage = styled.div`
	position: absolute;
	top: 8px;
	right: 12px;
	background: transparent;
	border: none;
	padding: 0;
	font-size: 12px;
	line-height: 1.2;
	z-index: 2;
	width: 220px;
`;

const defaultOptions = {
	responsive: true,
	maintainAspectRatio: false,
	parsing: {
		yAxisKey: "y",
	},
	scales: {
		x: {
			stacked: true,
			grid: {
				display: false,
			},
			ticks: {
				font: {
					size: 12,
					weight: "500",
				},
			},
		},
		y: {
			stacked: true,
			beginAtZero: true,
			grid: {
				color: "rgba(0, 0, 0, 0.1)",
			},
			ticks: {
				callback: (value) => `${value}`,
				font: {
					size: 12,
				},
			},
		},
	},
	plugins: {
		tooltip: {
			filter: function (tooltipItem) {
				return tooltipItem.datasetIndex === 1;
			},
			backgroundColor: "rgba(0, 0, 0, 0.8)",
			padding: 12,
			titleFont: {
				size: 14,
				weight: "bold",
			},
			bodyFont: {
				size: 13,
			},
			footerFont: {
				size: 13,
				weight: "bold",
			},
			displayColors: false
		},
		legend: {
			position: "top",
			align: "end",
			labels: {
				margin: 20,
				usePointStyle: true,
				pointStyle: "circle",
				font: {
					size: 12,
				},
			},
		},
	},
	interaction: {
		mode: "index",
		intersect: false,
	},
	animation: {
		duration: 750,
		easing: "easeInOutQuart",
		onComplete: function (animation) {
			// You can add post-animation callbacks here if needed
		},
	},
	onClick: (event, elements) => {},
};

const defaultDataSetConfig = [
	{
		backgroundColor: "rgba(54, 162, 235, 0.8)",
		// borderColor: "rgba(54, 162, 235, 1)",
		borderWidth: 2,
		borderSkipped:false,
		hoverOffset: 4,
		maxBarThickness: 40,
	},
	{
		backgroundColor: "rgba(75, 192, 192, 0.8)",
		// borderColor: "rgba(75, 192, 192, 1)",
		borderWidth: 2,
		borderSkipped:false,
		hoverOffset: 4,
		maxBarThickness: 40,
	},
];

//TODO: Type this for better config documentation.

const StackedBarChart = ({ labels, datasets, customOptions, styles, tooltipParsers, infoMessage }) => {
	const chartRef = useRef(null);
	const chartInstance = useRef(null);

	const chartOptions = useMemo(() => {
		let options = Object.assign({}, defaultOptions, customOptions);
		if (tooltipParsers) {
			setProperty(options, tooltipParsers, "plugins.tooltip.callbacks");
		}
		return options;
	}, [tooltipParsers, customOptions]);

	// Memoize processed datasets to prevent unnecessary recalculations
	const processedDatasets = useMemo(() => {
		let newDataSets = [];

		if (datasets.length) {
			for (const index in datasets) {
				const set = { ...defaultDataSetConfig[index], ...datasets[index] };
				newDataSets.push(set);
			}
		}

		return newDataSets;
	}, [datasets]);

	useEffect(() => {
		if (chartInstance.current) {
			chartInstance.current.destroy();
		}

		const ctx = chartRef.current.getContext("2d");

		// Create new chart instance
		chartInstance.current = new Chart(ctx, {
			type: "bar",
			data: { labels, datasets: processedDatasets },
			options: chartOptions,
			plugins: [ChartDataLabels],
		});

		// Cleanup function
		return () => {
			if (chartInstance.current) {
				chartInstance.current.destroy();
			}
		};
	}, [processedDatasets, chartOptions, labels]);

	return (
		<ChartContainer>
			<div style={{ height: "400px", position: "relative", ...styles }}>
				{infoMessage ? <InfoMessage>{infoMessage}</InfoMessage> : null}
				<canvas ref={chartRef} />
			</div>
		</ChartContainer>
	);
};

export default StackedBarChart;
